/**
 * Dashboard-specific types for the restructured Dashboard page
 * Part of the Web Portal restructuring (update.txt Parts 1-8)
 */

/**
 * TIR Summary Statistics for Part 2
 */
export interface TIRSummaryStats {
  targetRange: { min: number; max: number }
  tir: number // Time in Range % (Target zone)
  tbr: number // Time Below Range % (Low + Very Low)
  tar: number // Time Above Range % (High + Very High)
  avgGlucose: number // Average glucose in mmol/L
  dataCoverage: number // % of expected readings received
  periodStart: Date
  periodEnd: Date
}

/**
 * TIR Zone breakdown for detailed display
 */
export interface TIRZoneStats {
  veryHigh: number // > high boundary (species-dependent)
  high: number // targetMax+0.1 to high boundary (species-dependent)
  target: number // targetMin to targetMax (species-dependent)
  low: number // 3.0 to targetMin-0.1
  veryLow: number // <3.0 mmol/L
}

/**
 * AGP (Ambulatory Glucose Profile) data for Part 3
 * Shows glucose percentiles across a typical 24-hour period
 */
export interface AGPData {
  timeSlots: string[] // Hour labels: "00:00", "01:00", ..., "23:00"
  p5: number[] // 5th percentile
  p25: number[] // 25th percentile
  p50: number[] // 50th percentile (median)
  p75: number[] // 75th percentile
  p95: number[] // 95th percentile
  targetMin: number // Target range lower bound
  targetMax: number // Target range upper bound
}

/**
 * AGP Statistics for Part 3
 */
export interface AGPStatistics {
  gmi: number // Glucose Management Indicator (estimated HbA1c)
  cv: number // Coefficient of Variation
  avgGlucose: number // Average glucose
  stdDev: number // Standard deviation
  sensorActivePercent: number // % time sensor was active
}

/**
 * Low Glucose Event for Part 7
 */
export interface LowGlucoseEvent {
  id: string // Unique identifier
  timestamp: Date // When the event STARTED
  endTimestamp?: Date // Last sub-threshold reading of the episode
  glucose: number // Episode NADIR in mmol/L (worst reading, not the first)
  deviceId: number // Device that recorded the event
  deviceName?: string // Optional device name for display
  duration?: number // Duration below threshold in minutes (if consecutive readings)
  readingCount?: number // Sub-threshold readings making up this episode
}

/**
 * Daily Trend Data for Part 6
 */
export interface DailyTrendData {
  date: Date
  readings: Array<{ timestamp: Date; glucose: number }>
  min: number
  max: number
  avg: number
  readingCount: number
}

/**
 * Daily Activity Group for Part 6
 */
export interface DailyActivityGroup {
  date: Date
  // Dynamic bucket map keyed by canonical activity type. Pre-fix this was
  // a hardcoded shape with only insulin/food/exercise/water/urination/
  // vomiting/bgm — medicine (and sleep, hba1c, weight, signsOfIllness,
  // other) the mobile app emits all silently fell through the if-else
  // bucketing in DailyTrendActivitySection and never reached the panel,
  // even though they showed up as markers on the glucose chart. Keying
  // by string here means a new activity type the mobile app adds in the
  // future automatically gets a panel row without portal-side changes.
  activities: Record<string, ActivityEntry[]>
  totalCount: number
}

/**
 * Activity Entry (simplified for display)
 */
export interface ActivityEntry {
  id: number
  time: Date
  type: string
  description?: string
  details?: Record<string, unknown>
}

/**
 * Date range for report export (Part 8)
 */
export interface ExportDateRange {
  startDate: Date
  endDate: Date
}

/**
 * Species-specific glucose target ranges (mmol/L).
 * Based on veterinary reference values.
 *
 * - min/max: Target zone boundaries
 * - high: Upper boundary of the High zone (above this = Very High)
 */
export const SPECIES_TARGET_RANGES: Record<string, { min: number; max: number; high: number }> = {
  dogs: { min: 4.4, max: 11.1, high: 22.2 }, // Target 80-200 mg/dL, High ≤400, Very High >400
  cats: { min: 4.4, max: 16.7, high: 27.8 }, // Target 80-300 mg/dL, High ≤500, Very High >500
  horses: { min: 3.9, max: 6.6, high: 13.9 },
  rabbits: { min: 7.2, max: 12.0, high: 13.9 },
}

export const DEFAULT_SPECIES = 'dogs'

/**
 * Whether this species has a glucose band of its own.
 *
 * `getTargetRangeForSpecies` falls back to DOG values for anything absent from
 * `SPECIES_TARGET_RANGES`, silently. ada-core's Pets.species enum can store
 * cows, guinea_pigs and ferrets, none of which are backed — and prod does hold
 * guinea pigs (census 2026-08-29). Surface this wherever a vet is shown or asked
 * to edit a band, so a dog default is never mistaken for a species value.
 * See vic/vetpo#4 for the open question of what those ranges should be.
 */
export function isSpeciesBacked(species?: string | null): boolean {
  return species != null && Object.hasOwn(SPECIES_TARGET_RANGES, species)
}

/** Returns the target glucose range for a species, defaulting to dogs. */
export function getTargetRangeForSpecies(species?: string | null) {
  return SPECIES_TARGET_RANGES[species || DEFAULT_SPECIES] || SPECIES_TARGET_RANGES[DEFAULT_SPECIES]
}

/**
 * Glucose thresholds used throughout the dashboard
 * These should match the values in src/common.ts
 */
/**
 * LAST-RESORT fallbacks, in mmol/L — NOT the band a vet sees.
 *
 * The live band is resolved per patient by `usePetTargetRange(petId)`: pet
 * record (`target_glucose_low/high`, stored mg/dL) → per-pet RaStore override
 * → `SPECIES_TARGET_RANGES` → these. Every consumer below reads them as
 * `someResolvedValue ?? GLUCOSE_THRESHOLDS.X`, so they only take effect when
 * no pet/species context reached the component. Anywhere a clinical number is
 * shown to a vet, use the hook — a PDF export once read the species-only hook
 * while the on-screen panel layered the override, so the report handed to the
 * client disagreed with the screen.
 *
 * These ARE the dog defaults, deliberately: `getTargetRangeForSpecies(undefined)`
 * already resolves to `dogs` (DEFAULT_SPECIES), so a flat fallback that
 * disagreed with it produced two different answers for the same patient
 * depending only on whether species context reached the component.
 *
 * HIGH/VERY_HIGH were 13.9 until 2026-08-29 — the last residue of eng-brain
 * P13-1, where the chart painted 29.3% of prod dog readings red "Very High"
 * off a flat 13.9 while the TIR capsule directly above it counted them orange
 * "High" off dogs.high = 22.2. That fix taught the chart to honor a passed-in
 * boundary but left the fallback behind; this aligns it (Vic, 2026-08-29).
 *
 * ⚠️ Horses and rabbits cap at 13.9, so an unknown-species pet that is really
 * a horse is now under-flagged by this fallback rather than over-flagged. That
 * is the accepted trade: the species resolver already assumed dog, and one
 * consistent wrong answer beats two inconsistent ones on the same screen.
 */
export const GLUCOSE_THRESHOLDS = {
  VERY_LOW: 3.0, // fixed Very Low boundary — read flat everywhere, never overridden
  LOW: 4.4, // hypoglycemia boundary; also the targetMin fallback in some surfaces
  TARGET_MIN: 4.4, // target lower-bound fallback (= SPECIES_TARGET_RANGES.dogs.min)
  TARGET_MAX: 11.1, // target upper-bound fallback (= SPECIES_TARGET_RANGES.dogs.max)
  HIGH: 22.2, // `highMax ?? HIGH` — top of the High zone / floor of Very High
  VERY_HIGH: 22.2, // same boundary as HIGH, under the name the peak checks use.
  // HIGH and VERY_HIGH are ONE boundary under two names — move them together or
  // the chart's red band and the capsule's orange count disagree again (P13-1).
} as const

/**
 * TIR Zone colors following TIR Standardization v3.0
 * Order: Very High → High → Target → Low → Very Low
 */
export const TIR_ZONE_COLORS = {
  veryHigh: '#EF4444', // Red
  high: '#F59E0B', // Orange
  target: '#22C55E', // Green
  low: '#60A5FA', // Light Blue
  veryLow: '#6366F1', // Violet
} as const
